/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Input,
} from 'antd';
import { getPercepciones } from 'api/nominas/percepciones/percepciones';
import { checkToken } from 'api/utils';
import Select from 'components/Select';
import EditableTable from 'components/EditableTable';
import { onError } from 'utils/handlers';
import {
  getLiquidacionesEmpleadosIndemnizaciones,
  getLiquidacionEmpleadoIndemnizacion,
  patchLiquidacionEmpleadoIndemnizacion,
} from 'api/nominas/liquidaciones/liquidacionesEmpleadosIndemnizacion';
import { formatReceivedValues } from 'utils/formatValues';
import { getLiquidacionesUnidades } from 'api/nominas/liquidaciones/tipoLiquidacionUnidad';
import CurrencyInput from 'components/CurrencyInput';
import {
  FLOAT_NUMER_PATTERN,
} from 'utils/patterns';
import NumericInput from 'components/NumericInput';
import currencyFormatter from 'utils/currencyFormatter';

export const liquidacionEmpleadoIndemnizacionPermission = {
  permissionModel: 'liquidacionempleadopercepcion',
  catalogsModels: [],
  tabsModels: [],
};

// eslint-disable-next-line react/prop-types
const LiquidacionesEmpleadosIndemnizacion = ({ permission, liquidacionEmpleado, onClickEditEncabezado }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [percepciones, setPercecpiones] = useState();
  const [liquidacionesUnidades, setLiquidacionesUnidades] = useState();
  const [totalIndemnizacion, setTotalIndemnizacion] = useState(0);

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLiquidacionesEmpleadosIndemnizaciones({ liquidacion_empleado: liquidacionEmpleado, es_finiquito: false });
    setData(formatReceivedClone(_data));
    setTotalIndemnizacion(0);
    form.setFieldValue('importe_percepciones', 0);
    if (_data?.length) {
      let i = 0;
      let acum = 0;
      while (i < _data.length) {
        acum += _data[i].importe;
        i += 1;
      }
      setTotalIndemnizacion(acum);
      form.setFieldValue('importe_percepciones', acum);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _percepciones,
        _liquidacionesUnidades,
        _data,
      ] = await Promise.all([
        getPercepciones({ es_percepcion_de_indemnizacion: true }),
        getLiquidacionesUnidades(),
        getLiquidacionesEmpleadosIndemnizaciones({ liquidacion_empleado: liquidacionEmpleado, es_finiquito: false }),
      ]);
      if (mounted) {
        setPercecpiones(_percepciones);
        setLiquidacionesUnidades(_liquidacionesUnidades);
        setData(_data);
        setTotalIndemnizacion(0);
        form.setFieldValue('importe_percepciones', 0);
        if (_data?.length) {
          let i = 0;
          let acum = 0;
          while (i < _data.length) {
            acum += _data[i].importe;
            i += 1;
          }
          setTotalIndemnizacion(acum);
          form.setFieldValue('importe_percepciones', acum);
        }
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _liquidacionEmpleadoIndemnizacion = await getLiquidacionEmpleadoIndemnizacion(match.id);
    setTimeout(() => {
      if (_liquidacionEmpleadoIndemnizacion) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceivedClone(_liquidacionEmpleadoIndemnizacion));
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        liquidacion_empleado: liquidacionEmpleado,
      };
      const [key] = selectedRowKeys;
      const saved = await patchLiquidacionEmpleadoIndemnizacion(key, values);
      if (saved) {
        if (_continue) {
          await fetchData();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
          await onClickEditEncabezado();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
      );
    }
  };

  const handleOnRowClick = async (record) => {
    await form.validateFields();
    if (selectedRowKeys.length) {
      if (selectedRowKeys[0] === record.id) {
        return;
      }
      await onFinish();
    }
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
    valor: [
      requiredRule,
      {
        pattern: FLOAT_NUMER_PATTERN,
        message: 'Ingrese un valor válido',
      },
    ],
  };

  const columns = [
    {
      titleText: 'Percepción',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 300,
      render: (val, record) => {
        if (selectedRowKeys[0] === record.id) {
          return (
            <Form.Item
              name="percepcion"
              label="Percepción"
              rules={rules.required}
              hasFeedback
            >
              <Select
                disabled
                dataSource={percepciones}
                render={(e) => e?.descripcion}
              />
            </Form.Item>
          );
        }
        return <p>{percepciones.find((perc) => perc.id === val)?.descripcion}</p>;
      },
    },
    {
      titleText: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      width: 200,
      render: (val, record) => {
        if (selectedRowKeys[0] === record.id) {
          return (
            <Row>
              <Col span={20}>
                <NumericInput
                  name="cantidad"
                  label="Cantidad"
                  rules={rules.valor}
                  hasFeedback
                />
              </Col>
            </Row>
          );
        }
        return <p>{val}</p>;
      },
    },
    {
      titleText: 'Unidad',
      dataIndex: 'tipo_de_liquidacion_unidad',
      key: 'tipo_de_liquidacion_unidad',
      width: 200,
      render: (val, record) => {
        if (selectedRowKeys[0] === record.id) {
          return (
            <Form.Item
              name="tipo_de_liquidacion_unidad"
              label="Unidad"
              rules={rules.required}
              hasFeedback
            >
              <Select
                dataSource={liquidacionesUnidades}
              />
            </Form.Item>
          );
        }
        return <p>{liquidacionesUnidades.find((unidad) => unidad.id === val)?.descripcion}</p>;
      },
    },
    {
      titleText: 'Referencia',
      dataIndex: 'referencia',
      key: 'referencia',
      width: 300,
      render: (val, record) => {
        if (selectedRowKeys[0] === record.id) {
          return (
            <Form.Item
              name="referencia"
              label="Referencia"
              hasFeedback
            >
              <Input />
            </Form.Item>
          );
        }
        return <p>{val}</p>;
      },
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 200,
      align: 'right',
      render: (val, record) => {
        if (selectedRowKeys[0] === record.id) {
          return (
            <CurrencyInput
              name="importe"
              label="Importe"
              rules={rules.valor}
              hasFeedback
            />
          );
        }
        return <p>{currencyFormatter(val || 0)}</p>;
      },
    },
  ];

  const summaryCells = [
    {
      label: 'Total',
      value: totalIndemnizacion,
      valueAlign: 'right',
      render: (val) => currencyFormatter(val || 0),
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          name="general"
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onFinish();
            }
          }}
        >
          <EditableTable
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            allowSearch={false}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickFinish: () => onFinish(),
              onClickCancel: () => onCancel(),
            }}
            summaryCells={summaryCells}
            summaryColumnPosition={5}
            permission={permission}
          />
          <Col xs={24} sm={24} md={5}>
            <CurrencyInput
              name="importe_percepciones"
              label="Importe de Indemnización"
              disabled
            />
          </Col>
        </Form>
      </Spin>
    </Row>
  );
};

export default LiquidacionesEmpleadosIndemnizacion;
